import axios from 'axios'
import { AnyAction, Dispatch } from '@reduxjs/toolkit'
import { NavigateFunction } from 'react-router-dom'
import { LogoutUser } from '../redux/userredux/user_slice'
import { showLoader, hideLoader } from '../redux/userredux/loader_slice'

export const axiosInstance = axios.create({
  // other configurations
  baseURL:
    process.env.NODE_ENV === 'production'
      ? `https://${window.location.hostname}`
      : '',
  timeout: 15000, // Increased timeout to 15 seconds
  withCredentials: true // Enable withCredentials globally
  // headers: { Host: 'foodtollsindia.dev' }
})
console.log('axios base url', axiosInstance.defaults.baseURL)

// Flag to prevent multiple logout attempts
let isLoggingOut = false
let activeRequests = 0

// Variables for token refresh queue
let isRefreshing = false
let refreshPromise: Promise<any> | null = null
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

export const triggerProactiveRefresh = async () => {
  if (refreshPromise) {
    return refreshPromise
  }
  
  isRefreshing = true
  refreshPromise = axiosInstance.post('/api/auth/refresh', {}, { headers: { 'X-Hide-Loader': 'true' } })
    .then((res) => {
      isRefreshing = false
      refreshPromise = null
      processQueue(null)
      return res
    })
    .catch((err) => {
      isRefreshing = false
      refreshPromise = null
      processQueue(err)
      // 401 is handled by the response interceptor automatically
      throw err
    })
    
  return refreshPromise
}

// Store dispatch and navigate functions
let dispatchRef: Dispatch<AnyAction> | null = null
let navigateRef: NavigateFunction | null = null

// Set up request interceptor immediately (always active)
axiosInstance.interceptors.request.use(
  (config) => {
    // Check if we should show the loader for this request
    const showLoaderFlag = config.headers?.['X-Hide-Loader'] !== 'true' && !config.url?.includes('/auth/refresh')
    
    if (showLoaderFlag) {
      activeRequests++
      ;(config as any)._loaderTracked = true
      if (activeRequests === 1 && dispatchRef) {
        dispatchRef(showLoader())
      }
    }
    
    if (config.headers?.['X-Hide-Loader']) {
      delete config.headers['X-Hide-Loader']
    }

    // withCredentials is already set globally, but we can log for debugging
    console.log('Request config:', config)
    return config
  },
  (error) => {
    console.log('Request error:', error)
    return Promise.reject(error)
  }
)

// Set up response interceptor immediately (always active)
axiosInstance.interceptors.response.use(
  (response) => {
    if ((response.config as any)?._loaderTracked) {
      activeRequests = Math.max(0, activeRequests - 1)
      if (activeRequests === 0 && dispatchRef) {
        dispatchRef(hideLoader())
      }
    }
    console.log('Response:', response)
    // HttpOnly cookies are automatically managed by the browser
    // No need to manually extract them - they'll be sent with withCredentials: true
    return response
  },
  async (error) => {
    console.log('Response error in interceptor:', error)

    const originalRequest = error.config
    if (originalRequest && (originalRequest as any)._loaderTracked) {
      delete (originalRequest as any)._loaderTracked
      activeRequests = Math.max(0, activeRequests - 1)
      if (activeRequests === 0 && dispatchRef) {
        dispatchRef(hideLoader())
      }
    }

    // If the request was to the refresh endpoint itself and it failed with an auth/client error, we must logout
    if (originalRequest.url?.includes('/auth/refresh') && error.response && [400, 401, 403].includes(error.response.status)) {
      if (!isLoggingOut) {
        isLoggingOut = true
        console.log('Refresh failed - Triggering logout')
        if (dispatchRef && navigateRef) {
          dispatchRef(LogoutUser({}))
          navigateRef('/vendor/login')
        }
        setTimeout(() => {
          isLoggingOut = false
        }, 1000)
      }
      return Promise.reject(error)
    }

    // Check if we have a 401 error on a normal request
    if (error.response && error.response.status === 401) {

      // If we haven't already retried this request
      if (!originalRequest._retry) {
        if (isRefreshing || refreshPromise) {
          // If already refreshing, wait in the queue
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => axiosInstance(originalRequest))
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true

        try {
          console.log('Attempting to refresh token...')
          await triggerProactiveRefresh()
          // Retry original request
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          return Promise.reject(refreshError)
        }
      }
    }

    return Promise.reject(error)
  }
)

// Function to initialize the interceptor with dispatch and navigate
export const setupAxiosInterceptor = (
  dispatch: Dispatch<AnyAction>,
  navigate: NavigateFunction
) => {
  dispatchRef = dispatch
  navigateRef = navigate
  console.log('Axios interceptor initialized with dispatch and navigate')
}

// Hook for React components to call (simplified - just sets up refs)
const useAxiosInterceptor = (
  dispatch: Dispatch<AnyAction>,
  navigate: NavigateFunction
) => {
  setupAxiosInterceptor(dispatch, navigate)
}

export default useAxiosInterceptor

// src/components/AxiosInterceptor.tsx
// import React, { Children, useEffect } from "react";
// import axios, { AxiosResponse } from "axios";
// import { useDispatch } from "react-redux";
// import { useNavigate } from "react-router-dom";
// import { LogoutUser } from "../redux/userredux/user_slice";

// interface AxiosInterceptorProps {
//   children: React.ReactNode; // Ensure 'children' prop is defined
// }

// const axiosInstance = axios.create({
//   baseURL: "http://localhost:3000", // Adjust the baseURL as needed
//   withCredentials: true, // Enable withCredentials globally
// });

// const AxiosInterceptor: React.FC<AxiosInterceptorProps> = ({ children }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const requestInterceptor = axiosInstance.interceptors.request.use(
//       (config) => {
//         // Modify request config here if needed
//         return config;
//       },
//       (error) => {
//         return Promise.reject(error);
//       }
//     );

//     const responseInterceptor = axiosInstance.interceptors.response.use(
//       (response) => {
//         if (response.headers["set-cookie"]) {
//           const jwtToken = extractJwtTokenFromCookie(response);
//           localStorage.setItem("token", jwtToken);
//         }
//         return response;
//       },
//       (error) => {
//         if (error.response?.status === 401) {
//           dispatch(LogoutUser({}));
//           navigate("/vendor/login");
//         }
//         return Promise.reject(error);
//       }
//     );

//     // Clean up the interceptors on unmount
//     return () => {
//       axiosInstance.interceptors.request.eject(requestInterceptor);
//       axiosInstance.interceptors.response.eject(responseInterceptor);
//     };
//   }, [dispatch, navigate]);

//   // Function to extract JWT token from cookie
//   const extractJwtTokenFromCookie = (response: AxiosResponse) => {
//     const cookieHeader = response.headers["set-cookie"];
//     const token = cookieHeader
//       ? cookieHeader[0].split(";")[0].split("=")[1]
//       : "";
//     return token;
//   };

//   return <>{Children}</>;
// };

// export default AxiosInterceptor;
